/**
 * HDRPhoto dedicated monthly image-allowance plans.
 *
 * This is the client/seed catalogue. Live prices, Stripe price IDs and
 * recommended flags are stored in `public.hdr_plans` and can be edited in
 * HDR Admin without a rebuild. Apps should prefer `get_hdr_pricing()` and
 * fall back to these defaults if the RPC is unavailable.
 *
 * HDR Credits are a separate monthly balance from ReHub Credits.
 * One HDR Credit = one HDR edit. Do not mix this with the shared ReHub Credits wallet.
 *
 * GBP, EUR and USD are fixed regional prices — never convert with a live FX rate.
 */

import { formatMoneyFromMinor, type RehubCurrency } from './currency';

export type HdrPlanId =
  | 'free'
  | 'hdr_100'
  | 'hdr_250'
  | 'hdr_500'
  | 'hdr_1000'
  | 'hdr_2000'
  | 'hdr_5000';

export type HdrCurrency = RehubCurrency;

export interface HdrPlan {
  id: HdrPlanId;
  /** Short slider label, e.g. "500" or "1K". */
  sliderLabel: string;
  /** Customer-facing plan name. */
  name: string;
  imageAllowance: number;
  /** Monthly price in minor units (pence / cents). 0 = free. */
  priceGbpMinor: number;
  priceEurMinor: number;
  priceUsdMinor: number;
  /** Contact-sales / custom volume — no self-serve checkout. */
  isContact: boolean;
  isRecommended: boolean;
  displayOrder: number;
  isActive: boolean;
  stripePriceIdGbp?: string | null;
  stripePriceIdEur?: string | null;
  stripePriceIdUsd?: string | null;
}

export const HDR_FREE_ALLOWANCE = 10;
export const HDR_DEFAULT_PLAN_ID: HdrPlanId = 'hdr_500';
export const HDR_DEFAULT_PROCESSING_COST_USD = 0.3;
export const HDR_CONTACT_EMAIL = 'hello@rehub.studio';
export const HDR_APP_URL = 'https://hdrphoto.rehub.studio';
export const HDR_SIGNUP_URL = `${HDR_APP_URL}/login?mode=signup`;
export const HDR_PRICING_PATH = '/pricing';

export const HDR_PLANS: HdrPlan[] = [
  {
    id: 'free',
    sliderLabel: 'Free',
    name: 'Free',
    imageAllowance: HDR_FREE_ALLOWANCE,
    priceGbpMinor: 0,
    priceEurMinor: 0,
    priceUsdMinor: 0,
    isContact: false,
    isRecommended: false,
    displayOrder: 0,
    isActive: true,
  },
  {
    id: 'hdr_100',
    sliderLabel: '100',
    name: '100 HDR Credits',
    imageAllowance: 100,
    priceGbpMinor: 4900,
    priceEurMinor: 5900,
    priceUsdMinor: 6900,
    isContact: false,
    isRecommended: false,
    displayOrder: 1,
    isActive: true,
    stripePriceIdEur: 'price_1UESkjDHlg75FZHHzIEeG8Mv',
  },
  {
    id: 'hdr_250',
    sliderLabel: '250',
    name: '250 HDR Credits',
    imageAllowance: 250,
    priceGbpMinor: 9900,
    priceEurMinor: 11900,
    priceUsdMinor: 12900,
    isContact: false,
    isRecommended: false,
    displayOrder: 2,
    isActive: true,
    stripePriceIdEur: 'price_1UESkzDHlg75FZHHUNoPz0OO',
  },
  {
    id: 'hdr_500',
    sliderLabel: '500',
    name: '500 HDR Credits',
    imageAllowance: 500,
    priceGbpMinor: 17900,
    priceEurMinor: 21900,
    priceUsdMinor: 23900,
    isContact: false,
    isRecommended: true,
    displayOrder: 3,
    isActive: true,
    stripePriceIdEur: 'price_1UESl0DHlg75FZHHJQ750oaI',
  },
  {
    id: 'hdr_1000',
    sliderLabel: '1K',
    name: '1,000 HDR Credits',
    imageAllowance: 1000,
    priceGbpMinor: 33900,
    priceEurMinor: 41900,
    priceUsdMinor: 44900,
    isContact: false,
    isRecommended: false,
    displayOrder: 4,
    isActive: true,
    stripePriceIdEur: 'price_1UESl1DHlg75FZHHl5nn73Lq',
  },
  {
    id: 'hdr_2000',
    sliderLabel: '2K',
    name: '2,000 HDR Credits',
    imageAllowance: 2000,
    priceGbpMinor: 64900,
    priceEurMinor: 79900,
    priceUsdMinor: 86500,
    isContact: false,
    isRecommended: false,
    displayOrder: 5,
    isActive: true,
    stripePriceIdEur: 'price_1UESl3DHlg75FZHHH6FbsJSN',
  },
  {
    id: 'hdr_5000',
    sliderLabel: '5K+',
    name: '5,000+ HDR Credits',
    imageAllowance: 5000,
    priceGbpMinor: 0,
    priceEurMinor: 0,
    priceUsdMinor: 0,
    isContact: true,
    isRecommended: false,
    displayOrder: 6,
    isActive: true,
  },
];

export const HDR_PRICING_COPY = {
  heading: 'Simple HDR pricing',
  supporting:
    'Professional property HDR processing with straightforward monthly pricing. Choose the volume that works for your business.',
  ctaStart: 'Start processing',
  ctaContact: 'Contact us',
  ctaTryFree: 'Try 10 HDR Credits free',
  ctaViewPricing: 'View pricing',
  heroH1: 'Professional HDR. Simple pricing.',
  heroSupporting:
    'Fast, consistent property HDR processing for photographers and property professionals — built into the ReHub workflow.',
  usageLabel: 'HDR Credits',
  aiCreditsLabel: 'ReHub Credits',
  remainingOf: (remaining: number, allowance: number) =>
    `${remaining} of ${allowance} remaining`,
  remainingThisMonth: (remaining: number) =>
    `${remaining} HDR Credit${remaining === 1 ? '' : 's'} remaining this month`,
  renews: (dateLabel: string) => `Renews ${dateLabel}`,
  managePlan: 'Manage plan',
  freeVisible: `${HDR_FREE_ALLOWANCE} HDR Credits / month free`,
} as const;

export const HDR_WORKFLOW_STEPS = [
  { id: 'upload', label: 'Upload' },
  { id: 'hdrphoto', label: 'HDRPhoto' },
  { id: 'photoclear', label: 'PhotoClear' },
  { id: 'staging', label: 'Virtual Staging' },
  { id: 'hyperdusk', label: 'HyperDusk' },
  { id: 'imagemotion', label: 'ImageMotion' },
] as const;

export const HDR_CROSS_SELL = [
  {
    id: 'photoclear',
    name: 'PhotoClear',
    description: 'Enhance and clean the image',
    url: 'https://photoclear.rehub.studio',
    uses: 'ai_credits' as const,
  },
  {
    id: 'staging',
    name: 'Virtual Staging',
    description: 'Furnish an empty room',
    url: 'https://staging.rehub.studio',
    uses: 'ai_credits' as const,
  },
  {
    id: 'hyperdusk',
    name: 'HyperDusk',
    description: 'Create a twilight version',
    url: 'https://hyperdusk.rehub.studio',
    uses: 'ai_credits' as const,
  },
  {
    id: 'imagemotion',
    name: 'ImageMotion',
    description: 'Turn the photograph into cinematic video',
    url: 'https://imagemotion.rehub.studio',
    uses: 'ai_credits' as const,
  },
] as const;

export function getHdrPlan(id: string | null | undefined): HdrPlan | undefined {
  return HDR_PLANS.find((plan) => plan.id === id);
}

export function getRecommendedHdrPlan(plans: HdrPlan[] = HDR_PLANS): HdrPlan {
  const list = plans.length ? plans : HDR_PLANS;
  return (
    list.find((plan) => plan.isRecommended && plan.isActive) ||
    list.find((p) => p.id === HDR_DEFAULT_PLAN_ID) ||
    list[0] ||
    HDR_PLANS.find((p) => p.id === HDR_DEFAULT_PLAN_ID)!
  );
}

export function getActiveHdrPlans(plans?: HdrPlan[] | null): HdrPlan[] {
  const source = plans?.length ? plans : HDR_PLANS;
  const active = source.filter((plan) => plan.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
  return active.length ? active : HDR_PLANS.filter((plan) => plan.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
}

/** Smallest active plan whose monthly allowance covers the credits needed. */
export function suggestHdrPlanForCredits(creditsNeeded: number, plans?: HdrPlan[] | null): HdrPlan {
  const list = getActiveHdrPlans(plans);
  const needed = Math.max(0, creditsNeeded);
  const selfServe = list.filter((plan) => !plan.isContact);
  const fit = selfServe.find((plan) => plan.imageAllowance >= needed);
  if (fit) return fit;
  return list.find((plan) => plan.isContact) ?? selfServe[selfServe.length - 1] ?? getRecommendedHdrPlan(list);
}

export function hdrPlanPriceMinor(plan: HdrPlan, currency: HdrCurrency): number {
  if (currency === 'USD') return plan.priceUsdMinor;
  if (currency === 'EUR') return plan.priceEurMinor;
  return plan.priceGbpMinor;
}

export function formatHdrMoney(minor: number, currency: HdrCurrency): string {
  return formatMoneyFromMinor(minor, currency);
}

export function formatHdrMonthlyPrice(plan: HdrPlan, currency: HdrCurrency): string {
  if (plan.isContact) return 'Contact us';
  const minor = hdrPlanPriceMinor(plan, currency);
  if (minor <= 0) return formatHdrMoney(0, currency);
  return `${formatHdrMoney(minor, currency)} / month`;
}

/**
 * Effective price per processed HDR image for the selected plan.
 * GBP under £1 is shown in pence (e.g. "36p per image").
 * EUR and USD use two-decimal currency amounts (e.g. "€0.44 per image").
 */
export function formatHdrPerImagePrice(plan: HdrPlan, currency: HdrCurrency): string | null {
  if (plan.isContact) return null;
  const minor = hdrPlanPriceMinor(plan, currency);
  if (minor <= 0 || plan.imageAllowance <= 0) return null;
  const perMinor = minor / plan.imageAllowance;
  if (currency === 'GBP' && perMinor < 100) {
    return `${Math.round(perMinor)}p per edit`;
  }
  return `${formatMoneyFromMinor(perMinor, currency, { decimals: 2 })} per edit`;
}

export function formatHdrAllowance(allowance: number, opts?: { plus?: boolean }): string {
  if (opts?.plus || allowance >= 5000) {
    if (allowance >= 5000) return '5,000+ HDR Credits / month';
  }
  const formatted = allowance.toLocaleString('en-GB');
  return `${formatted} HDR Credit${allowance === 1 ? '' : 's'} / month`;
}

export function hdrLookupKey(planId: string, currency: HdrCurrency): string {
  return `hdrphoto_${planId}_monthly_${currency.toLowerCase()}`;
}

export function parseHdrPlanId(raw: string | null | undefined): HdrPlanId | null {
  if (!raw) return null;
  return HDR_PLANS.some((plan) => plan.id === raw) ? (raw as HdrPlanId) : null;
}

export function mergeHdrPlans(remote: Partial<HdrPlan>[] | null | undefined): HdrPlan[] {
  if (!remote?.length) return HDR_PLANS;
  const byId = new Map(remote.map((plan) => [plan.id, plan]));
  return HDR_PLANS.map((seed) => {
    const overlay = byId.get(seed.id);
    if (!overlay) return seed;
    return {
      ...seed,
      ...overlay,
      id: seed.id,
      sliderLabel: overlay.sliderLabel || seed.sliderLabel,
      name: overlay.name || seed.name,
    };
  }).sort((a, b) => a.displayOrder - b.displayOrder);
}

export function envHdrPriceOverride(planId: string, currency: HdrCurrency): string | undefined {
  if (typeof process === 'undefined' || !process.env) return undefined;
  const key = `STRIPE_PRICE_HDR_${planId.toUpperCase()}_MONTHLY_${currency}`;
  const viteKey = `VITE_${key}`;
  return process.env[key] || process.env[viteKey] || undefined;
}
